"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";

export interface MapLevelBoundaryMetadataPatch {
  name?: string;
  note?: string;
}

export interface MapLevelBoundaryPanelHandlers {
  getDraftFeatureByBoundaryId: (
    boundaryId: string,
  ) => GeoJSON.Feature<GeoJSON.Polygon> | null;
  updateBoundaryMetadata: (
    boundaryId: string,
    patch: MapLevelBoundaryMetadataPatch,
  ) => void;
}

interface MapLevelBoundaryPanelContextValue {
  selectedBoundaryId: string | null;
  setSelectedBoundaryId: (boundaryId: string | null) => void;
  clearSelectedBoundary: () => void;
  getDraftFeatureByBoundaryId: (
    boundaryId: string,
  ) => GeoJSON.Feature<GeoJSON.Polygon> | null;
  updateBoundaryMetadata: (
    boundaryId: string,
    patch: MapLevelBoundaryMetadataPatch,
  ) => void;
  registerHandlers: (handlers: MapLevelBoundaryPanelHandlers | null) => void;
}

const MapLevelBoundaryPanelContext =
  createContext<MapLevelBoundaryPanelContextValue | null>(null);

export function useMapLevelBoundaryPanel() {
  const context = useContext(MapLevelBoundaryPanelContext);
  if (!context) {
    throw new Error(
      "useMapLevelBoundaryPanel must be used inside <MapLevelBoundaryPanelProvider />",
    );
  }
  return context;
}

export function useMapLevelBoundaryPanelOptional() {
  return useContext(MapLevelBoundaryPanelContext);
}

export const MapLevelBoundaryPanelProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [selectedBoundaryId, setSelectedBoundaryIdState] = useState<string | null>(null);
  const handlersRef = useRef<MapLevelBoundaryPanelHandlers | null>(null);

  const setSelectedBoundaryId = useCallback((boundaryId: string | null) => {
    setSelectedBoundaryIdState(boundaryId);
  }, []);

  const clearSelectedBoundary = useCallback(() => {
    setSelectedBoundaryIdState(null);
  }, []);

  const registerHandlers = useCallback((handlers: MapLevelBoundaryPanelHandlers | null) => {
    handlersRef.current = handlers;
  }, []);

  const getDraftFeatureByBoundaryId = useCallback((boundaryId: string) => {
    return handlersRef.current?.getDraftFeatureByBoundaryId(boundaryId) ?? null;
  }, []);

  const updateBoundaryMetadata = useCallback(
    (boundaryId: string, patch: MapLevelBoundaryMetadataPatch) => {
      handlersRef.current?.updateBoundaryMetadata(boundaryId, patch);
    },
    [],
  );

  const value = useMemo<MapLevelBoundaryPanelContextValue>(
    () => ({
      selectedBoundaryId,
      setSelectedBoundaryId,
      clearSelectedBoundary,
      getDraftFeatureByBoundaryId,
      updateBoundaryMetadata,
      registerHandlers,
    }),
    [
      clearSelectedBoundary,
      getDraftFeatureByBoundaryId,
      registerHandlers,
      selectedBoundaryId,
      setSelectedBoundaryId,
      updateBoundaryMetadata,
    ],
  );

  return (
    <MapLevelBoundaryPanelContext.Provider value={value}>
      {children}
    </MapLevelBoundaryPanelContext.Provider>
  );
};
